import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import { createContact } from '@actions/contacts';
import { createNote, selectNote, updateNote } from '@actions/notes';
import { alertUser } from '@actions/alerts';
import NoteEditor from '@components/Note/Editor';
import Contact from '@models/Contact';
import ContactSearch from './ContactSearch';
import styles from './NoteModal.module.scss';
import importAllFiles from '@support/importAllFiles';
import StateBasedFadeTransition from '@components/transitions/StateBasedFadeTransition';
import Modal from '@components/Modals';
import { ContractNoteIcon, ExpandNoteIcon, LoaderIcon, XIcon } from '@components/Icons';
import Occasion from '@support/Occasion';
import { NoteDateDisplay } from '@components/Note/NoteDetails';
import {
  closeModalAndClearNoteDraft,
  contactAndNoteModalStateSelector,
  updateContactAndNoteModal,
} from '@actions/ui';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@hooks/use-theme';
import { useAuthContext } from '@hooks/use-auth-context';
import { useContactLimitExceeded } from '@support/hooks/use-contact-limit-exceeded';
import NoteIconAndColorPicker from '@components/Note/IconAndColorPicker';
import NoteLabelEditor from '@components/Note/LabelEditor';
import Note, { NoteType } from '@models/Note';
import { InteractionAccesory } from '@components/ContactCard';
import { ReminderBadgeIcon } from '@components/Icons/Reminder';

const icons = importAllFiles(import.meta.glob('/public/img/contact/icons/source/*.svg'));

const Title = ({ children }) => (
  <label htmlFor="note" className="form-label">
    {children}
  </label>
);

function NoteTypeEditor({
  note,
  onChange,
  onLabelEnterKey,
}: {
  note: Note;
  onChange: (noteType: NoteType) => void;
  onLabelEnterKey?: () => void;
}) {
  if (note.isReadOnly) {
    return (
      <div className={styles.noteTypeEditor}>
        <InteractionAccesory interaction="contacts" />
        <div className={styles.noteLabel}>Note via {note.importSource}</div>
      </div>
    );
  }

  return (
    <div className={styles.noteTypeEditor}>
      {note?.reminder?.date ? (
        <ReminderBadgeIcon date={note.reminder.date} />
      ) : (
        <NoteIconAndColorPicker noteType={note.type} updateNoteType={onChange} />
      )}
      <NoteLabelEditor
        label={note.type.label}
        updateLabel={(label) => onChange({ ...note.type, label })}
        onEnterKey={onLabelEnterKey}
      />
    </div>
  );
}

function ComposeHeader({ children = null, addingPerson, isSaving }) {
  const [theme] = useTheme();
  const contactLimitExceeded = useContactLimitExceeded();

  let title = 'Add New Person or Find Existing';
  if (contactLimitExceeded) {
    title = 'Find an Existing Person';
  }

  let state = 'addingNote';
  if (addingPerson && isSaving) {
    state = 'saving';
  } else if (addingPerson && !isSaving) {
    state = 'addingPerson';
  }

  return (
    <div className={styles.header}>
      <div className={styles.headerTitleBar}>
        <StateBasedFadeTransition
          state={state}
          elementByStateMap={{
            addingNote: <Title>Person</Title>,
            addingPerson: (
              <>
                <Title>{title}</Title>
                <div className={styles.sourceIcons}>
                  {['email', 'linkedin', 'twitter', 'phone'].map((icon) => (
                    <img key={icon} src={icons[`${icon}_${theme}`] || icons[icon]} />
                  ))}
                </div>
              </>
            ),
            saving: (
              <>
                <Title>Saving New Person</Title>
                <LoaderIcon className={styles.loader} />
              </>
            ),
          }}
        />
      </div>
      {children}
    </div>
  );
}

export default function ContactAndNoteModal(props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { contactIsRestricted } = useAuthContext();
  const contactLimitExceeded = useContactLimitExceeded();
  const { contact, note, canEditContact, focusMode, creatingNewReminder } = useSelector(
    contactAndNoteModalStateSelector,
  );
  const noteInStore = useSelector(selectNote(note?.id));
  const [savingNewContact, setSavingNewContact] = useState(false);
  const [enablingFocusMode, setEnablingFocusMode] = useState(false);
  const [focusModeEnabled, setFocusModeEnabled] = useState(false);
  const [focusModeDisabled, setFocusModeDisabled] = useState(false);
  const [showSavingState, setShowSavingState] = useState(false);

  const contactInputRef = useRef(null);
  const noteInputRef = useRef(null);
  const inputsDidMount = useRef(false);
  const formIsValid =
    (contact.isPersisted && !note.isEmpty) ||
    (!contactLimitExceeded && !contact.isPersisted && contact.displayName.length > 0);

  const initialNoteState = useMemo(() => note.copyWith({}), []);

  // set focus once inputs are mounted
  useEffect(() => {
    if (
      !inputsDidMount.current &&
      (contactInputRef.current || contact.isPersisted) &&
      noteInputRef.current
    ) {
      if (!contact.isPersisted) {
        contactInputRef.current.focus();
      } else {
        noteInputRef.current.focus();
      }

      inputsDidMount.current = true;
    } else if (contactInputRef.current && !contact.isPersisted) {
      // focus the contact input if the selected contact is removed
      contactInputRef.current.focus();
    }
  }, [contactInputRef.current, noteInputRef.current]);

  useEffect(() => {
    if (focusMode) {
      enableFocusMode();
    }
  }, []);

  const enableFocusMode = () => {
    setEnablingFocusMode(true);
    setTimeout(() => {
      setFocusModeEnabled(true);
      setEnablingFocusMode(false);
    }, 100);
    noteInputRef.current?.focus();
  };

  const disableFocusMode = () => {
    setEnablingFocusMode(true);
    setTimeout(() => {
      setFocusModeEnabled(false);
      setEnablingFocusMode(false);
      setFocusModeDisabled(true);
    }, 100);
    noteInputRef.current?.focus();
  };

  const focusNoteInput = () => noteInputRef.current?.focus();

  useEffect(() => {
    if (focusModeDisabled) {
      setTimeout(() => setFocusModeDisabled(false), 150);
    }
  }, [focusModeDisabled]);

  useEffect(() => {
    if (contact.isPersisted && savingNewContact) {
      dispatch(closeModalAndClearNoteDraft);
      navigate(contact.link, { replace: true });
    }
  }, [contact.isPersisted, navigate]);

  useEffect(() => {
    if (contact && contactIsRestricted(contact)) {
      dispatch(updateContactAndNoteModal({ contact: new Contact() }));
    }
  }, []);

  const handleNoteContentChange = (content: string) => {
    dispatch(updateContactAndNoteModal({ note: note.copyWith({ content }) }));
  };

  const handleNoteReminderChange = (date: Occasion | null) => {
    const existingReminder = note.reminder;
    let reminder = existingReminder ? { ...existingReminder, date } : { date };

    if (!date) {
      reminder = null;
    }

    dispatch(updateContactAndNoteModal({ note: note.copyWith({ reminder }) }));
  };

  const selectExistingContact = (selectedContact) => {
    dispatch(
      updateContactAndNoteModal({
        contact: selectedContact ? selectedContact : new Contact(),
      }),
    );
    if (noteInputRef.current) {
      noteInputRef.current.focus();
    }
  };

  const handleBlur = (e) => {
    e.target.blur();
  };

  // if user has selected an existing contact as newContact, this just creates a new note for that contact
  // if user is creating a new contact, we dispatch creation of that contact and queue up note posting for when
  // that request completes (handled in epic)
  const saveContactAndNote = (e) => {
    if (e?.defaultPrevented) {
      return;
    }

    if (savingNewContact) {
      return;
    }

    if (!formIsValid) {
      if (contact.isPersisted && note.content.length == 0) {
        dispatch(alertUser('Please add a note first', 'error'));
      } else if (!contact.isPersisted && contact.displayName.length == 0) {
        dispatch(alertUser('Please enter a person', 'error'));
      }
      return;
    }

    if (contact.isPersisted) {
      if (note.isPersisted) {
        dispatch({ type: 'UPDATE_NOTE_IN_TIMELINE', payload: { note, contactID: contact.id } });

        if (!note.hasSameValues(initialNoteState)) {
          dispatch(updateNote(note));
        }

        if (!focusModeEnabled) {
          props.closeModal();
        } else {
          setShowSavingState(true);
        }
      } else {
        dispatch(createNote(note.copyWith({ contact })));
        dispatch(closeModalAndClearNoteDraft);
      }
    } else {
      setSavingNewContact(true);
      if (note.content.length > 0) {
        dispatch(createContact({ contact, note }));
      } else {
        dispatch(createContact({ contact }));
      }
    }
  };

  const closeModal = (e) => {
    if (note.isPersisted) {
      saveContactAndNote(e);
    }
    props.closeModal?.();
  };

  const noteTypeEditor = (
    <NoteTypeEditor
      note={note}
      onChange={(noteType: NoteType) =>
        dispatch(updateContactAndNoteModal({ note: note.copyWith({ type: noteType }) }))
      }
      onLabelEnterKey={focusNoteInput}
    />
  );

  useEffect(() => {
    if (showSavingState && !noteInStore?.isFetching) {
      const timer = setTimeout(() => setShowSavingState(false), 2000);
      return () => clearTimeout(timer);
    }

    return () => {};
  }, [showSavingState, noteInStore?.isFetching]);

  return (
    <Modal
      isOpen={true}
      containerClassName={styles.container}
      className={cn(
        enablingFocusMode && !focusModeEnabled && styles.focusing,
        focusModeEnabled && styles.focusMode,
        focusMode && styles.straightToFocusMode,
      )}
      overlayClassName={cn(
        enablingFocusMode && focusModeEnabled && styles.unfocusing,
        focusModeDisabled && styles.focusModeDisabled,
      )}
      onRequestClose={closeModal}
    >
      <ComposeHeader addingPerson={!contact.isPersisted} isSaving={savingNewContact}>
        <ContactSearch
          ref={contactInputRef}
          contact={contact.isPersisted ? contact : null}
          onLookup={(displayName) =>
            dispatch(updateContactAndNoteModal({ contact: { displayName } }))
          }
          onContact={selectExistingContact}
          onSubmit={saveContactAndNote}
          disabled={savingNewContact}
          contactNotEditable={!canEditContact}
        />
        <div className={styles.headerButtons}>
          {contact.isPersisted && (
            <button
              className={styles.headerBtn}
              disabled={enablingFocusMode}
              onClick={() => {
                if (focusModeEnabled) {
                  disableFocusMode();
                } else {
                  enableFocusMode();
                }
              }}
            >
              {focusModeEnabled ? <ContractNoteIcon /> : <ExpandNoteIcon />}
            </button>
          )}
          <button
            className={styles.headerBtn}
            onClick={closeModal}
            disabled={savingNewContact}
            aria-label="Close"
          >
            <XIcon />
          </button>
        </div>
      </ComposeHeader>
      <div className={styles.noteContainer}>
        <NoteEditor
          className={cn(styles.noteEditor, savingNewContact && styles.saving)}
          footerClassName={styles.noteEditorFooter}
          note={note}
          onNoteContentChange={handleNoteContentChange}
          onNoteReminderChange={handleNoteReminderChange}
          inputRef={noteInputRef}
          onNoteSave={saveContactAndNote}
          onBlur={handleBlur}
          showSaveBtn={formIsValid}
          saveDisabled={!formIsValid || savingNewContact}
          noteSaving={showSavingState && noteInStore?.isFetching}
          noteSavedState={
            showSavingState && !noteInStore?.isFetching && (noteInStore?.error ? 'error' : 'saved')
          }
          large
          ignoreOutsideClicks
          defaultReminderEditorIsOpen={creatingNewReminder}
          header={
            !focusModeEnabled ? (
              <>
                <label htmlFor="note" className={cn('form-label', styles.labelRow)}>
                  Note
                  {note.isPersisted && (
                    <div className="information">
                      <NoteDateDisplay date={note.created} />
                    </div>
                  )}
                </label>
                {noteTypeEditor}
              </>
            ) : (
              noteTypeEditor
            )
          }
        />
      </div>
    </Modal>
  );
}
